/**
 * Sign Up Form Component
 *
 * A multi-step form for creating new user accounts with tenant creation and Plaid integration.
 * Flow: Form → Signup → Plaid Link → Success
 *
 * User information and tenant information are collected separately:
 * - Section 1: User Information (personal details)
 * - Section 2: Tenant/Business Information (organization details)
 *
 * @fileoverview Sign up form with Supabase authentication and Plaid integration
 */

import React, { useState, FormEvent, useCallback, useEffect } from 'react';
import {
  VStack,
  FormControl,
  FormLabel,
  FormErrorMessage,
  FormHelperText,
  Input,
  Button,
  InputGroup,
  InputRightElement,
  IconButton,
  Text,
  Link as ChakraLink,
  Alert,
  AlertIcon,
  AlertDescription,
  useColorModeValue,
  HStack,
  Divider,
  List,
  ListItem,
  ListIcon,
  Spinner,
  Center,
  Box,
  Heading,
  Select,
  SimpleGrid,
} from '@chakra-ui/react';
import { supabase } from '../../lib/supabase';

import { ViewIcon, ViewOffIcon, CheckCircleIcon, WarningIcon, LockIcon } from '@chakra-ui/icons';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  validateEmail,
  validatePassword,
  validateName,
  validateConfirmPassword,
  validatePhoneE164,
  formatPhoneE164,
} from '../../utils/validation';
import AuthLayout from './AuthLayout';
import { publicApi } from '../../services/adminApi';
import { signupApi, SignupResult, SignupApiError, TenantInfo } from '../../services/signupApi';
import { SignupPlaidStep, SignupSuccessStep } from './signup';

// US States for dropdown
const US_STATES = [
  { value: '', label: 'Select State' },
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
  { value: 'DC', label: 'District of Columbia' },
];

/**
 * Signup flow steps
 */
type SignupStep = 'form' | 'submitting' | 'plaid' | 'success';

/**
 * Props for SignUpForm component
 */
interface SignUpFormProps {
  /** Callback function called on successful sign up */
  onSuccess?: () => void;
}

/**
 * Sign Up Form Component
 *
 * Multi-step registration flow:
 * 1. Form - User enters details (user info + tenant info)
 * 2. Submitting - Account creation in progress
 * 3. Plaid - Bank connection (optional)
 * 4. Success - Redirect to dashboard
 */
export const SignUpForm: React.FC<SignUpFormProps> = ({ onSuccess }) => {
  const [searchParams] = useSearchParams();

  // Step management
  const [step, setStep] = useState<SignupStep>('form');
  const [signupResult, setSignupResult] = useState<SignupResult | null>(null);
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [bankConnected, setBankConnected] = useState(false);

  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Invite token state
  const [inviteToken, setInviteToken] = useState<string | null>(null);
  const [inviteValidating, setInviteValidating] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState<string | null>(null);
  // For tenant-specific invites (joining existing tenant)
  const [inviteTenantId, setInviteTenantId] = useState<string | null>(null);
  // For standalone invites (no tenant, admin assigns later)
  const [isStandaloneInvite, setIsStandaloneInvite] = useState(false);

  // =========================================================================
  // USER INFORMATION STATE
  // =========================================================================
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [userPhoneNumber, setUserPhoneNumber] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // =========================================================================
  // TENANT/BUSINESS INFORMATION STATE
  // =========================================================================
  const [tenantName, setTenantName] = useState('');
  const [tenantEin, setTenantEin] = useState('');
  const [tenantAddress, setTenantAddress] = useState('');
  const [tenantAddressLine2, setTenantAddressLine2] = useState('');
  const [tenantCity, setTenantCity] = useState('');
  const [tenantState, setTenantState] = useState('');
  const [tenantZipCode, setTenantZipCode] = useState('');
  const [tenantPhoneNumber, setTenantPhoneNumber] = useState('');
  const [tenantWebsite, setTenantWebsite] = useState('');
  const [tenantManagerName, setTenantManagerName] = useState('');
  const [tenantManagerEmail, setTenantManagerEmail] = useState('');

  // Validation state
  const [errors, setErrors] = useState({
    // User fields
    firstName: null as string | null,
    lastName: null as string | null,
    email: null as string | null,
    userPhoneNumber: null as string | null,
    password: null as string | null,
    confirmPassword: null as string | null,
    // Tenant fields
    tenantName: null as string | null,
    tenantEin: null as string | null,
    tenantWebsite: null as string | null,
    tenantManagerEmail: null as string | null,
  });
  const [touched, setTouched] = useState({
    // User fields
    firstName: false,
    lastName: false,
    email: false,
    userPhoneNumber: false,
    password: false,
    confirmPassword: false,
    // Tenant fields
    tenantName: false,
    tenantEin: false,
    tenantWebsite: false,
    tenantManagerEmail: false,
  });

  // Check for invite token on mount
  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      setInviteToken(token);
      setInviteValidating(true);

      publicApi.invites.validateToken(token)
        .then((result) => {
          if (result.valid && result.email) {
            setInviteEmail(result.email);
            setEmail(result.email);
            // Check invite type
            if (result.inviteType === 'STANDALONE') {
              // Standalone invite - no tenant, admin assigns later
              setIsStandaloneInvite(true);
            } else if (result.inviteType === 'TENANT_MEMBER' && result.tenantId) {
              // Tenant-specific invite - joining existing tenant
              setInviteTenantId(result.tenantId);
            }
            // NEW_TENANT: user creates account + new tenant — no extra state needed,
            // skipTenantInfo stays false so the business form is shown
          } else {
            setInviteError(result.reason || 'Invalid or expired invite');
          }
        })
        .catch(() => {
          setInviteError('Failed to validate invite token');
        })
        .finally(() => {
          setInviteValidating(false);
        });
    } else {
      setInviteError('An invitation is required to create an account. Please contact an administrator.');
    }
  }, [searchParams]);

  // Color mode values
  const linkColor = useColorModeValue('brand.secondary', 'brand.secondaryLight');
  const successColor = useColorModeValue('green.500', 'green.300');
  const errorColor = useColorModeValue('red.500', 'red.300');
  const readOnlyBg = useColorModeValue('gray.100', 'xacto.mutedNavy');
  const sectionBg = useColorModeValue('gray.50', 'xacto.mutedNavy');

  // Password requirements
  const passwordRequirements = [
    { met: password.length >= 8, text: 'At least 8 characters' },
    { met: /[A-Z]/.test(password), text: 'One uppercase letter' },
    { met: /[0-9]/.test(password), text: 'One number' },
  ];

  /**
   * Validate EIN format (XX-XXXXXXX)
   */
  const validateEin = (ein: string): string | null => {
    if (!ein) return null;
    if (!/^\d{2}-\d{7}$/.test(ein)) {
      return 'EIN must be in format XX-XXXXXXX';
    }
    return null;
  };

  /**
   * Validate URL format
   */
  const validateUrl = (url: string): string | null => {
    if (!url) return null;
    try {
      new URL(url);
      return null;
    } catch {
      return 'Please enter a valid URL (e.g., https://example.com)';
    }
  };

  // Skip tenant info for: tenant-specific invites (joining existing) OR standalone invites (no tenant)
  const skipTenantInfo = inviteTenantId || isStandaloneInvite;

  /**
   * Validate all form fields
   */
  const validateForm = useCallback((): boolean => {
    const newErrors = {
      // User validation
      firstName: validateName(firstName, 'First name'),
      lastName: validateName(lastName, 'Last name'),
      email: validateEmail(email),
      userPhoneNumber: userPhoneNumber ? validatePhoneE164(userPhoneNumber) : null,
      password: validatePassword(password),
      confirmPassword: validateConfirmPassword(password, confirmPassword),
      // Tenant validation - skip for tenant-specific invites OR standalone invites
      tenantName: skipTenantInfo ? null : (tenantName.trim() ? null : 'Business name is required'),
      tenantEin: skipTenantInfo ? null : validateEin(tenantEin),
      tenantWebsite: skipTenantInfo ? null : validateUrl(tenantWebsite),
      tenantManagerEmail: skipTenantInfo ? null : (tenantManagerEmail ? validateEmail(tenantManagerEmail) : null),
    };

    setErrors(newErrors);
    return !Object.values(newErrors).some((err) => err !== null);
  }, [firstName, lastName, email, userPhoneNumber, password, confirmPassword, tenantName, tenantEin, tenantWebsite, tenantManagerEmail, skipTenantInfo]);

  /**
   * Handle field blur for validation
   */
  const handleBlur = useCallback((field: keyof typeof touched) => {
    setTouched((prev) => ({ ...prev, [field]: true }));

    switch (field) {
      // User fields
      case 'firstName':
        setErrors((prev) => ({ ...prev, firstName: validateName(firstName, 'First name') }));
        break;
      case 'lastName':
        setErrors((prev) => ({ ...prev, lastName: validateName(lastName, 'Last name') }));
        break;
      case 'email':
        setErrors((prev) => ({ ...prev, email: validateEmail(email) }));
        break;
      case 'userPhoneNumber':
        setErrors((prev) => ({ ...prev, userPhoneNumber: userPhoneNumber ? validatePhoneE164(userPhoneNumber) : null }));
        break;
      case 'password':
        setErrors((prev) => ({ ...prev, password: validatePassword(password) }));
        break;
      case 'confirmPassword':
        setErrors((prev) => ({
          ...prev,
          confirmPassword: validateConfirmPassword(password, confirmPassword),
        }));
        break;
      // Tenant fields
      case 'tenantName':
        setErrors((prev) => ({ ...prev, tenantName: tenantName.trim() ? null : 'Business name is required' }));
        break;
      case 'tenantEin':
        setErrors((prev) => ({ ...prev, tenantEin: validateEin(tenantEin) }));
        break;
      case 'tenantWebsite':
        setErrors((prev) => ({ ...prev, tenantWebsite: validateUrl(tenantWebsite) }));
        break;
      case 'tenantManagerEmail':
        setErrors((prev) => ({ ...prev, tenantManagerEmail: tenantManagerEmail ? validateEmail(tenantManagerEmail) : null }));
        break;
    }
  }, [firstName, lastName, email, userPhoneNumber, password, confirmPassword, tenantName, tenantEin, tenantWebsite, tenantManagerEmail]);

  /**
   * Handle form submission
   */
  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setError(null);

      // Mark all fields as touched
      setTouched({
        firstName: true,
        lastName: true,
        email: true,
        userPhoneNumber: true,
        password: true,
        confirmPassword: true,
        tenantName: true,
        tenantEin: true,
        tenantWebsite: true,
        tenantManagerEmail: true,
      });

      if (!validateForm()) {
        return;
      }

      if (!inviteToken) {
        setError('No invite token found');
        return;
      }

      setLoading(true);
      setStep('submitting');

      try {
        // Build tenant info object only for regular invites (creating new tenant)
        // For tenant-specific invites or standalone invites, we don't send tenant info
        const tenantInfo: TenantInfo | undefined = skipTenantInfo ? undefined : {
          name: tenantName.trim(),
          ein: tenantEin.trim() || undefined,
          address: tenantAddress.trim() || undefined,
          addressLine2: tenantAddressLine2.trim() || undefined,
          city: tenantCity.trim() || undefined,
          state: tenantState || undefined,
          zipCode: tenantZipCode.trim() || undefined,
          country: 'US',
          phoneNumber: tenantPhoneNumber.trim() ? formatPhoneE164(tenantPhoneNumber) : undefined,
          website: tenantWebsite.trim() || undefined,
          managerName: tenantManagerName.trim() || undefined,
          managerEmail: tenantManagerEmail.trim() || undefined,
        };

        const result = await signupApi.complete({
          token: inviteToken,
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: email.trim(),
          userPhoneNumber: userPhoneNumber.trim() ? formatPhoneE164(userPhoneNumber) : undefined,
          password,
          tenant: tenantInfo,
        });

        setSignupResult(result);

        // Store the signup tenant ID so UserContext selects the correct tenant
        // For standalone invites, tenantId may be null
        if (result.tenantId) {
          localStorage.setItem('xacto_signup_tenant_id', result.tenantId);
        }

        // Set session in Supabase client to log user in
        const { error: sessionError } = await supabase.auth.setSession({
          access_token: result.accessToken,
          refresh_token: result.refreshToken,
        });

        if (sessionError) {
          console.error('Failed to set session:', sessionError);
          // Don't fail the whole flow, but user might need to log in manually
        } else {
             // AuthContext will detect this change and trigger MFA check if needed
        }

        // For standalone invites (no tenant), skip Plaid and go to success
        if (isStandaloneInvite || !result.tenantId) {
          setStep('success');
          return;
        }

        // Get Plaid link token
        try {
          const linkResult = await signupApi.createLinkToken(result.tenantId, result.accessToken);

          setLinkToken(linkResult.linkToken);
          setStep('plaid');
        } catch (linkError) {
          console.error('Failed to create link token:', linkError);
          setStep('success');
        }
      } catch (err) {
        console.error('Signup failed:', err);
        if (err instanceof SignupApiError) {
          setError(err.message);
        } else if (err instanceof Error) {
          setError(err.message);
        } else {
          setError('An unexpected error occurred');
        }
        setStep('form');
      } finally {
        setLoading(false);
      }
    },
    [firstName, lastName, email, userPhoneNumber, password, inviteToken, validateForm, tenantName, tenantEin, tenantAddress, tenantAddressLine2, tenantCity, tenantState, tenantZipCode, tenantPhoneNumber, tenantWebsite, tenantManagerName, tenantManagerEmail, inviteTenantId]
  );

  const handlePlaidSuccess = useCallback(() => {
    setBankConnected(true);
    setStep('success');
  }, []);

  const handlePlaidSkip = useCallback(() => {
    setBankConnected(false);
    setStep('success');
  }, []);

  const togglePasswordVisibility = useCallback(() => {
    setShowPassword((prev) => !prev);
  }, []);

  const toggleConfirmPasswordVisibility = useCallback(() => {
    setShowConfirmPassword((prev) => !prev);
  }, []);

  // Loading state
  if (inviteValidating) {
    return (
      <AuthLayout
        title="Validating Invite"
        subtitle="Please wait while we verify your invitation"
      >
        <Center py={10}>
          <VStack spacing={4}>
            <Spinner size="xl" color={linkColor} thickness="4px" />
            <Text color="gray.500">Validating your invite...</Text>
          </VStack>
        </Center>
      </AuthLayout>
    );
  }

  // Error state
  if (inviteError) {
    return (
      <AuthLayout
        title="Invalid Invite"
        subtitle="There was a problem with your invitation"
      >
        <VStack spacing={6}>
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            <AlertDescription>{inviteError}</AlertDescription>
          </Alert>
          <Text fontSize="sm" textAlign="center">
            This invitation link may have expired or already been used.
            Please contact an administrator to request a new invite.
          </Text>
          <ChakraLink
            as={RouterLink}
            to="/"
            color={linkColor}
            fontWeight="medium"
          >
            Return to Sign In
          </ChakraLink>
        </VStack>
      </AuthLayout>
    );
  }

  // Submitting state
  if (step === 'submitting') {
    return (
      <AuthLayout
        title="Creating Your Account"
        subtitle="Please wait while we set everything up"
      >
        <Center py={10}>
          <VStack spacing={4}>
            <Spinner size="xl" color="blue.500" thickness="4px" />
            <Text color="gray.500">Setting up your account and organization...</Text>
          </VStack>
        </Center>
      </AuthLayout>
    );
  }

  // Plaid step
  // Plaid step - only shown when tenantId exists (not for standalone invites)
  if (step === 'plaid' && linkToken && signupResult && signupResult.tenantId) {
    return (
      <AuthLayout
        title="Connect Your Bank"
        subtitle="Link your bank account to get started"
      >
        <SignupPlaidStep
          linkToken={linkToken}
          tenantId={signupResult.tenantId}
          accessToken={signupResult.accessToken}
          onSuccess={handlePlaidSuccess}
          onSkip={handlePlaidSkip}
        />
      </AuthLayout>
    );
  }

  // Success step
  if (step === 'success') {
    return (
      <AuthLayout
        title="Welcome to Xacto App"
        subtitle="Your account is ready"
      >
        <SignupSuccessStep
          bankConnected={bankConnected}
          redirectPath="/home"
          autoRedirectDelay={5}
        />
      </AuthLayout>
    );
  }

  // Form
  return (
    <AuthLayout
      title="Complete Your Registration"
      subtitle="You've been invited to join Xacto App"
    >
      <form onSubmit={handleSubmit}>
        <VStack spacing={6}>
          {/* Invite Info Alert */}
          {inviteEmail && (
            <Alert status="info" borderRadius="md">
              <AlertIcon />
              <Box>
                <AlertDescription>
                  {inviteTenantId ? (
                    <>You've been invited to join an existing organization. Sign up with <strong>{inviteEmail}</strong></>
                  ) : isStandaloneInvite ? (
                    <>You've been invited to create an account. Sign up with <strong>{inviteEmail}</strong>. An administrator will assign you to an organization later.</>
                  ) : (
                    <>You've been invited to create your account and organization on Xacto App. Sign up with <strong>{inviteEmail}</strong></>
                  )}
                </AlertDescription>
              </Box>
            </Alert>
          )}

          {/* Error Alert */}
          {error && (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* ================================================================= */}
          {/* SECTION 1: USER INFORMATION */}
          {/* ================================================================= */}
          <Box w="full" bg={sectionBg} p={5} borderRadius="lg">
            <Heading size="sm" mb={4} color="accent.primary">
              Your Information
            </Heading>

            <VStack spacing={4}>
              {/* Name Fields */}
              <HStack spacing={4} w="full">
                <FormControl isInvalid={touched.firstName && !!errors.firstName}>
                  <FormLabel>First Name *</FormLabel>
                  <Input
                    type="text"
                    placeholder="John"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    onBlur={() => handleBlur('firstName')}
                    autoComplete="given-name"
                  />
                  <FormErrorMessage>{errors.firstName}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={touched.lastName && !!errors.lastName}>
                  <FormLabel>Last Name *</FormLabel>
                  <Input
                    type="text"
                    placeholder="Doe"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    onBlur={() => handleBlur('lastName')}
                    autoComplete="family-name"
                  />
                  <FormErrorMessage>{errors.lastName}</FormErrorMessage>
                </FormControl>
              </HStack>

              {/* Email Field */}
              <FormControl isInvalid={touched.email && !!errors.email}>
                <FormLabel>Email *</FormLabel>
                <InputGroup>
                  <Input
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => !inviteEmail && setEmail(e.target.value)}
                    onBlur={() => handleBlur('email')}
                    autoComplete="email"
                    isReadOnly={!!inviteEmail}
                    bg={inviteEmail ? readOnlyBg : undefined}
                    cursor={inviteEmail ? 'not-allowed' : undefined}
                  />
                  {inviteEmail && (
                    <InputRightElement>
                      <LockIcon color="gray.400" />
                    </InputRightElement>
                  )}
                </InputGroup>
                {inviteEmail && (
                  <FormHelperText color="gray.500">
                    Email is set from your invitation and cannot be changed.
                  </FormHelperText>
                )}
                <FormErrorMessage>{errors.email}</FormErrorMessage>
              </FormControl>

              {/* User Phone Number */}
              <FormControl isInvalid={touched.userPhoneNumber && !!errors.userPhoneNumber}>
                <FormLabel>
                  Personal Phone{' '}
                  <Text as="span" color="gray.500" fontSize="sm" fontWeight="normal">
                    (optional)
                  </Text>
                </FormLabel>
                <Input
                  type="tel"
                  placeholder="555 123 4567"
                  value={userPhoneNumber}
                  onChange={(e) => setUserPhoneNumber(e.target.value)}
                  onBlur={() => handleBlur('userPhoneNumber')}
                  autoComplete="tel"

                />
                <FormHelperText>US number (e.g., 555 123 4567) or international (+44 ...)</FormHelperText>
                <FormErrorMessage>{errors.userPhoneNumber}</FormErrorMessage>
              </FormControl>

              {/* Password Fields */}
              <FormControl isInvalid={touched.password && !!errors.password}>
                <FormLabel>Password *</FormLabel>
                <InputGroup>
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Create a strong password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onBlur={() => handleBlur('password')}
                    autoComplete="new-password"
  
                  />
                  <InputRightElement>
                    <IconButton
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                      onClick={togglePasswordVisibility}
                      variant="ghost"
                      size="sm"
                    />
                  </InputRightElement>
                </InputGroup>
                <FormErrorMessage>{errors.password}</FormErrorMessage>
                {password.length > 0 && (
                  <List spacing={1} mt={2}>
                    {passwordRequirements.map((req, index) => (
                      <ListItem
                        key={index}
                        fontSize="xs"
                        color={req.met ? successColor : errorColor}
                      >
                        <ListIcon
                          as={req.met ? CheckCircleIcon : WarningIcon}
                          color={req.met ? successColor : errorColor}
                        />
                        {req.text}
                      </ListItem>
                    ))}
                  </List>
                )}
              </FormControl>

              <FormControl isInvalid={touched.confirmPassword && !!errors.confirmPassword}>
                <FormLabel>Confirm Password *</FormLabel>
                <InputGroup>
                  <Input
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Confirm your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    onBlur={() => handleBlur('confirmPassword')}
                    autoComplete="new-password"
  
                  />
                  <InputRightElement>
                    <IconButton
                      aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                      icon={showConfirmPassword ? <ViewOffIcon /> : <ViewIcon />}
                      onClick={toggleConfirmPasswordVisibility}
                      variant="ghost"
                      size="sm"
                    />
                  </InputRightElement>
                </InputGroup>
                <FormErrorMessage>{errors.confirmPassword}</FormErrorMessage>
              </FormControl>
            </VStack>
          </Box>

          {/* ================================================================= */}
          {/* SECTION 2: TENANT/BUSINESS INFORMATION */}
          {/* Only shown for regular invites (creating new tenant) */}
          {/* Hidden for: tenant-specific invites (joining existing tenant) */}
          {/*             standalone invites (no tenant, admin assigns later) */}
          {/* ================================================================= */}
          {!skipTenantInfo && (
          <Box w="full" bg={sectionBg} p={5} borderRadius="lg">
            <Heading size="sm" mb={4} color="accent.success">
              Business Information
            </Heading>

            <VStack spacing={4}>
              {/* Business Name */}
              <FormControl isInvalid={touched.tenantName && !!errors.tenantName}>
                <FormLabel>Business Name *</FormLabel>
                <Input
                  type="text"
                  placeholder="Acme Corporation"
                  value={tenantName}
                  onChange={(e) => setTenantName(e.target.value)}
                  onBlur={() => handleBlur('tenantName')}

                />
                <FormErrorMessage>{errors.tenantName}</FormErrorMessage>
              </FormControl>

              {/* EIN */}
              <FormControl isInvalid={touched.tenantEin && !!errors.tenantEin}>
                <FormLabel>
                  EIN (Employer Identification Number){' '}
                  <Text as="span" color="gray.500" fontSize="sm" fontWeight="normal">
                    (optional)
                  </Text>
                </FormLabel>
                <Input
                  type="text"
                  placeholder="12-3456789"
                  value={tenantEin}
                  onChange={(e) => setTenantEin(e.target.value)}
                  onBlur={() => handleBlur('tenantEin')}

                />
                <FormHelperText>Format: XX-XXXXXXX</FormHelperText>
                <FormErrorMessage>{errors.tenantEin}</FormErrorMessage>
              </FormControl>

              {/* Address */}
              <FormControl>
                <FormLabel>
                  Street Address{' '}
                  <Text as="span" color="gray.500" fontSize="sm" fontWeight="normal">
                    (optional)
                  </Text>
                </FormLabel>
                <Input
                  type="text"
                  placeholder="123 Main Street"
                  value={tenantAddress}
                  onChange={(e) => setTenantAddress(e.target.value)}

                />
              </FormControl>

              <FormControl>
                <FormLabel>
                  Address Line 2{' '}
                  <Text as="span" color="gray.500" fontSize="sm" fontWeight="normal">
                    (optional)
                  </Text>
                </FormLabel>
                <Input
                  type="text"
                  placeholder="Suite 100"
                  value={tenantAddressLine2}
                  onChange={(e) => setTenantAddressLine2(e.target.value)}

                />
              </FormControl>

              {/* City, State, Zip */}
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} w="full">
                <FormControl>
                  <FormLabel>City</FormLabel>
                  <Input
                    type="text"
                    placeholder="San Francisco"
                    value={tenantCity}
                    onChange={(e) => setTenantCity(e.target.value)}
  
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>State</FormLabel>
                  <Select
                    value={tenantState}
                    onChange={(e) => setTenantState(e.target.value)}
  
                  >
                    {US_STATES.map((state) => (
                      <option key={state.value} value={state.value}>
                        {state.label}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel>ZIP Code</FormLabel>
                  <Input
                    type="text"
                    placeholder="94105"
                    value={tenantZipCode}
                    onChange={(e) => setTenantZipCode(e.target.value)}
  
                  />
                </FormControl>
              </SimpleGrid>

              {/* Business Phone */}
              <FormControl>
                <FormLabel>
                  Business Phone{' '}
                  <Text as="span" color="gray.500" fontSize="sm" fontWeight="normal">
                    (optional)
                  </Text>
                </FormLabel>
                <Input
                  type="tel"
                  placeholder="555 123 4567"
                  value={tenantPhoneNumber}
                  onChange={(e) => setTenantPhoneNumber(e.target.value)}

                />
              </FormControl>

              {/* Website */}
              <FormControl isInvalid={touched.tenantWebsite && !!errors.tenantWebsite}>
                <FormLabel>
                  Website{' '}
                  <Text as="span" color="gray.500" fontSize="sm" fontWeight="normal">
                    (optional)
                  </Text>
                </FormLabel>
                <Input
                  type="url"
                  placeholder="https://www.example.com"
                  value={tenantWebsite}
                  onChange={(e) => setTenantWebsite(e.target.value)}
                  onBlur={() => handleBlur('tenantWebsite')}

                />
                <FormErrorMessage>{errors.tenantWebsite}</FormErrorMessage>
              </FormControl>

              <Divider />

              {/* Account Manager */}
              <Text fontSize="sm" fontWeight="medium" color="gray.600" alignSelf="flex-start">
                Account Manager (optional)
              </Text>

              <HStack spacing={4} w="full">
                <FormControl>
                  <FormLabel>Manager Name</FormLabel>
                  <Input
                    type="text"
                    placeholder="Jane Smith"
                    value={tenantManagerName}
                    onChange={(e) => setTenantManagerName(e.target.value)}
  
                  />
                </FormControl>

                <FormControl isInvalid={touched.tenantManagerEmail && !!errors.tenantManagerEmail}>
                  <FormLabel>Manager Email</FormLabel>
                  <Input
                    type="email"
                    placeholder="manager@example.com"
                    value={tenantManagerEmail}
                    onChange={(e) => setTenantManagerEmail(e.target.value)}
                    onBlur={() => handleBlur('tenantManagerEmail')}
  
                  />
                  <FormErrorMessage>{errors.tenantManagerEmail}</FormErrorMessage>
                </FormControl>
              </HStack>
            </VStack>
          </Box>
          )}

          {/* Submit Button */}
          <Button
            type="submit"
            colorScheme="blue"
            size="lg"
            w="full"
            isLoading={loading}
            loadingText="Creating account..."
          >
            Create Account
          </Button>

          {/* Divider */}
          <HStack w="full">
            <Divider />
            <Text fontSize="sm" color="gray.500" whiteSpace="nowrap">
              or
            </Text>
            <Divider />
          </HStack>

          {/* Sign In Link */}
          <Text fontSize="sm" textAlign="center">
            Already have an account?{' '}
            <ChakraLink
              as={RouterLink}
              to="/"
              color={linkColor}
              fontWeight="medium"
            >
              Sign in
            </ChakraLink>
          </Text>
        </VStack>
      </form>
    </AuthLayout>
  );
};

export default SignUpForm;
